import { useRef, useEffect } from 'react';
import Text12 from '@sdk/components/ui/typography/Text12';
import gettext from '@sdk/lang/gettext';
import Switcher from '@sdk/components/forms/Switcher';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import Tooltip, { hideTooltip, showTooltip } from '@sdk/common/tooltip/Tooltip';
import useAuth from '@sdk/authentication/useAuth';

import { useShouldShow } from '@web/common/hooks/useShouldShow';
import { ConfirmationTooltip } from '@web/components/ConfirmationTooltip';
import { ApplyFiltersContainer } from '@web/shifts/ActivityLog/ApplyScheduleFilters/applyScheduleFilters.styles';

type Props = {
  applyScheduleFilters: boolean;
  setApplyScheduleFilters: (value: boolean) => void;
  isOpen: boolean;
};

export const ApplyScheduleFilters = ({
  applyScheduleFilters,
  setApplyScheduleFilters,
  isOpen,
}: Props) => {
  const tooltipRef = useRef<HTMLDivElement>();

  const { currentUser } = useAuth();
  const { dontShowAgain, shouldShow } = useShouldShow(
    'activityLogScheduleFiltersTooltip',
    currentUser
  );

  useEffect(() => {
    if (isOpen && shouldShow()) {
      setTimeout(() => {
        showTooltip(tooltipRef.current);
      }, 500);
    } else {
      hideTooltip(tooltipRef.current);
    }
  }, [isOpen, shouldShow]);

  return (
    <>
      <ApplyFiltersContainer
        ref={tooltipRef}
        data-for="activity-log-schedule-filters-tip"
        data-tip
        data-place="left">
        <Text12 themeColor={ThemeColorType.primaryText}>{gettext('Apply schedule filters')}</Text12>
        <Switcher
          value={applyScheduleFilters}
          onChange={(value: boolean): void => setApplyScheduleFilters(value)}
          small
        />
        <Tooltip
          id="activity-log-schedule-filters-tip"
          clickable={true}
          attachToBody
          event='"none"'
          eventOff='"none"'
          getContent={() => (
            <ConfirmationTooltip
              content={gettext(
                'When on, date range and schedule filters will be applied to narrow down activity.'
              )}
              onClick={() => {
                hideTooltip(tooltipRef.current);
                dontShowAgain();
              }}
            />
          )}
          minWidth={266}
        />
      </ApplyFiltersContainer>
    </>
  );
};
